import type { ActionTree, GetterTree, MutationTree } from 'vuex';
import { DESIRED_CLIENTS_COUNT, checkIfProductExists } from './helpers/portfolio';
import type { RootState } from '~/types/store/index';
import type { PortfolioState } from '~/types/store/portfolio';
import { sortByDateField } from '~/components/utils/sorting_func';
import { withRollback } from '~/components/utils/store';
import { has } from '~/components/utils/core/object';
import type { RequestState } from '~/types/common';
import type { Portfolio } from '~/types/portfolio';
import type { UpdatePortfolioUserResponse, PortfolioUserResponse } from '~/types/api';

const getInitialState = (): PortfolioState => ({
  isPersonal: null,
  user: {},
  clients: [],
  testimonials: [],
  testifySign: null,
  token: '',
  topUsers: [],
  tooltips: [],
  departments: [],
  workHistories: [],
  productTypes: [],
  awards: [],
  configuration: {
    is_public: false,
    section_awards_is_public: false,
    section_clients_is_public: false,
    section_contacts_is_public: false,
    section_headline_is_public: false,
    section_sales_experience_is_public: false,
    section_testimonials_is_public: false,
    section_work_histories_is_public: false,
  },
  war_room_industries: [],
  contacts: { email: '' },
  linkedInImportState: 'idle',
  linkedInImportRetriesCount: 0,
  pending: true,
  loading: false,
  avatarLoading: false,
});

export const state = getInitialState;

export const getters: GetterTree<PortfolioState, RootState> = {
  hasAvatar(state) {
    return state.user.avatar_exists;
  },
  visibleClientsPublic(state) {
    return state.clients.filter(client => client.public);
  },
  hasClients(state, getters): boolean {
    return getters.visibleClientsPublic.length > 0;
  },
  hasEnoughClients(state, getters): boolean {
    return getters.visibleClientsPublic.length >= DESIRED_CLIENTS_COUNT;
  },
  hasDescription(state): boolean {
    return !!state.user.description;
  },
  visibleWorkHistories(_state, getters) {
    return getters.sortedWorkHistories.filter((history: Portfolio.Workhistory) => history.public);
  },
  sortedWorkHistories(state) {
    const sortedList = state.workHistories.slice().sort(sortByDateField('start_date'));

    const currentList = sortedList.filter(history => history.current_workplace);
    const pastList = sortedList.filter(history => !history.current_workplace);

    return [...currentList, ...pastList.sort(sortByDateField('end_date'))];
  },
  hasWorkHistories(state): boolean {
    return state.workHistories.length > 0;
  },
  hasSalesExperience(state, getters): boolean {
    return (
      state.productTypes.length > 0 || getters.selectedDepartments.length > 0 || state.war_room_industries!.length > 0
    );
  },
  hasAwards(state): boolean {
    return state.awards.length > 0;
  },
  selectedDepartments(state) {
    return state.departments.filter(d => !!d.value);
  },
  isPortfolioEmpty(state, getters): boolean {
    return !(
      (state.configuration.section_clients_is_public && getters.hasClients) ||
      (state.configuration.section_headline_is_public && getters.hasDescription) ||
      (state.configuration.section_awards_is_public && getters.hasAwards) ||
      (state.configuration.section_sales_experience_is_public && getters.hasSalesExperience) ||
      (state.configuration.section_work_histories_is_public && getters.hasWorkHistories)
    );
  },
  isTooltipVisible(_state, _getters, rootState) {
    return (tooltip: string) => {
      if (!rootState.auth.user) return true;
      return !rootState.auth.user.tooltips?.includes(tooltip);
    };
  },
};

export const mutations: MutationTree<PortfolioState> = {
  updateState(state, newState) {
    Object.assign(state, newState);
  },
  addClient(state, client) {
    state.clients.push(client);
  },
  updateClient(state, { id, data }) {
    const client = state.clients.find(client => client.id === id);

    if (client) Object.assign(client, data);
  },
  moveClient(state, { oldIndex, newIndex }) {
    const tmpClients = state.clients.slice();

    tmpClients.splice(newIndex, 0, tmpClients.splice(oldIndex, 1)[0]);

    state.clients = tmpClients;
  },
  toggleLoading(state, loading) {
    state.loading = loading;
  },
  updateUser(state, payload) {
    Object.assign(state.user, payload);
  },
  deleteClient(state, clientId) {
    state.clients = state.clients.filter(({ id }) => id !== clientId);
  },
  setDepartments(state, departments) {
    state.departments = departments;
  },
  addDepartment(state, department) {
    state.departments.push(department);
  },
  updateDepartment(state, department) {
    state.departments = state.departments.map(d => (d.id === department.id ? { ...d, ...department } : d));
  },
  removeDepartment(state, department) {
    state.departments = state.departments.filter(d => d.id !== department.id);
  },
  setProductTypes(state, productTypes) {
    state.productTypes = productTypes;
  },
  addProductType(state, productType) {
    state.productTypes.push(productType);
  },
  removeProductType(state, productType) {
    state.productTypes = state.productTypes.filter(pt => pt.id !== productType.id);
  },
  setWarRoomIndustries(state, warRoomIndustries) {
    state.war_room_industries = warRoomIndustries;
  },
  updateModel(state, { model, id, data }) {
    // @ts-ignore
    const item = state[model].find(i => i.id === id);

    if (item) {
      Object.assign(item, data);
    }
  },
  addItem(state, { model, item }) {
    // @ts-ignore
    state[model].push(item);
  },
  removeItem(state, { model, id }) {
    // @ts-ignore
    state[model] = state[model].filter(({ id: itemId }) => itemId !== id);
  },
  resetState(state) {
    Object.assign(state, getInitialState());
  },
  updatePortfolioConfiguration(state, payload) {
    Object.assign(state.configuration, payload.configuration);
  },
  updateLinkedInImportState(state, newState: RequestState) {
    state.linkedInImportState = newState;
  },
  incrementLinkedInRetriesCount(state) {
    state.linkedInImportRetriesCount++;
  },
};

export const actions: ActionTree<PortfolioState, RootState> = {
  async load({ commit }, { slug }) {
    const INITIAL_LOADING_STATE = {
      loading: false,
      avatarLoading: false,
    };
    commit('toggleLoading', true);

    const {
      clients = [],
      awards = [],
      user,
      isPersonal,
      token,
      topUsers = [],
      departments = [],
      workHistories = [],
      tooltips = [],
      product_types: productTypes = [],
      configuration,
      war_room_industries = [],
      contacts,
      pending,
    } = await this.$api.$get<PortfolioUserResponse>(`/p/${slug}/api.json`);

    commit('updateState', {
      isPersonal,
      user,
      awards,
      clients,
      token,
      topUsers,
      tooltips,
      departments,
      workHistories,
      productTypes,
      configuration,
      war_room_industries,
      contacts,
      pending,
      ...INITIAL_LOADING_STATE,
    });
  },
  async closeTooltip(_, tooltip) {
    const user = {
      ...this.$auth.user,
      tooltips: [...this.$auth.user.tooltips!, tooltip],
    };

    this.$auth.$storage.setState('user', user);
    await this.$api.$get(`/profile/tooltips/complete.json?task=${tooltip}`);
  },
  async generateUserDescription({ state, commit }) {
    if (!state.user.description) {
      const { user } = await this.$api.$put('/profile/generate_user_description.json');

      commit('updateUser', { description: user.description });
    }
  },
  async addWorkHistory({ commit }, payload) {
    const workHistory = await this.$api.$post('/api/work_histories.json', payload);
    commit('addItem', { model: 'workHistories', item: workHistory });
  },
  updateWorkHistory: withRollback(async function ({ commit }, payload) {
    commit('updateModel', { model: 'workHistories', id: payload.id, data: payload });

    const workHistory = await this.$api.$put(`/api/work_histories/${payload.id}.json`, payload);
    commit('updateModel', { model: 'workHistories', id: payload.id, data: workHistory });
  }),
  removeWorkHistory: withRollback(async function ({ commit }, payload) {
    commit('removeItem', { model: 'workHistories', id: payload.id });
    await this.$api.$delete(`/api/work_histories/${payload.id}.json`);
  }),
  async addDepartment({ commit, dispatch }, name) {
    const department = await dispatch('createDepartment', name);
    commit('addDepartment', department);
    return department;
  },
  async createDepartment({ state }, name) {
    if (state.departments.find(d => d.name.toLowerCase() === name.toLowerCase())) {
      throw new Error('This department is already added');
    }

    return await this.$api.$post('/api/departments.json', { name });
  },
  toggleDeparment: withRollback(async function ({ commit }, { department, attach }) {
    if (department.custom && !attach) {
      commit('removeDepartment', department);
    } else {
      commit('updateDepartment', { ...department, value: attach });
    }

    const path = attach
      ? `/api/departments/${department.id}/attach.json`
      : `/api/departments/${department.id}/detach.json`;

    if (attach) {
      await this.$api.$post(path, {});
    } else {
      await this.$api.$delete(path);
    }
  }),
  async addProductType({ commit, dispatch }, name) {
    const productType = await dispatch('createProductType', name);

    commit('addProductType', productType);
  },
  async createProductType({ state }, name) {
    checkIfProductExists(state, name);

    return await this.$api.$post('/api/product_types.json', { name });
  },
  async updateUser({ state, commit }, payload) {
    const response = await this.$api.$put<UpdatePortfolioUserResponse>('/profile.json', payload);

    // if payload contains user's public email, then update contacts property in state
    if (has(payload.user, 'public_email')) {
      commit('updateState', { contacts: { email: payload.user.public_email || '' } });
    }

    // if slug has changed we need to update current user for navigation to work correctly
    if (state.user.slug !== response.api.user.slug) {
      const user = {
        ...this.$auth.user,
        slug: response.api.user.slug,
      };
      this.$auth.$storage.setState('user', user);
    }

    commit('updateUser', response.api.user);
  },
  toggleProductType: withRollback(async function ({ state, commit }, { productType, attach }) {
    if (attach) {
      checkIfProductExists(state, productType.name);

      commit('addProductType', productType);
    } else {
      commit('removeProductType', productType);
    }
    const path = attach
      ? `/api/product_types/${productType.id}/attach.json`
      : `/api/product_types/${productType.id}/detach.json`;

    if (attach) {
      await this.$api.$post(path, {});
    } else {
      await this.$api.$delete(path);
    }
  }),
  addClient: withRollback(async function ({ state, commit }, client) {
    if (state.clients.find(c => c.company_id === client.id)) {
      throw new Error('This client is already added');
    }

    const {
      client_id: clientId,
      api: { clients },
    } = await this.$api.$post('/clients.json', { user_client: client });
    commit(
      'addClient',
      (clients as Array<Portfolio.Client>).find(c => c.id === clientId),
    );
  }),
  removeClient: withRollback(async function ({ commit }, client) {
    commit('removeItem', { model: 'clients', id: client.id });
    await this.$api.$delete(`/clients/${client.id}.json`);
  }),
  updateClient: withRollback(async function ({ commit }, payload) {
    commit('updateClient', { id: payload.id, data: payload });
    await this.$api.$patch(`/clients/${payload.id}.json`, { user_client: payload });
  }),
  attachPurpose: withRollback(async function (_store, id) {
    await this.$api.$post(`/api/profile/purposes/${id}/attach`);
  }),
  detachPurpose: withRollback(async function (_store, id) {
    await this.$api.$delete(`/api/profile/purposes/${id}/detach`);
  }),
  uploadClientLogo: withRollback(async function ({ commit }, { clientId, payload }) {
    const { avatar_url } = await this.$axios.$post('/api/clients/upload_logo.json', payload);
    commit('updateClient', { id: clientId, data: { avatar_url } });
  }),
  saveAwards: withRollback(async function ({ commit }, payload) {
    const { awards } = await this.$api.$post('/api/portfolio/awards/sync.json', { awards: payload });
    commit('updateState', { awards });
  }),
  async updatePortfolioConfig({ commit }, payload) {
    await this.$api.$patch('/api/portfolio/configuration.json', payload);

    commit('updatePortfolioConfiguration', payload);
  },
};
